import {
  charsFontWidth12pixelsSize500Map,
  charsFontWidth9pixelsSize500Map,
} from '../../text-ellipsis/chars-font-width-map.js';
import { calculateWidth } from '../../text-ellipsis/truncate-string.js';
import {
  CONTEXT_VALUE_MAX_WIDTH,
  CONTEXT_VALUE_SIGN_COLUMN_HEIGHT_THRESHOLD,
  CONTEXT_VALUE_SIGN_COLUMN_LARGE,
  CONTEXT_VALUE_SIGN_COLUMN_SMALL,
  CONTEXT_VALUE_TRAILING_PADDING,
  FONT_SIZE_BREAKING_POINT,
  NEGATIVE_SIGN,
} from '../constants.js';
import type { SparklinLabelsAlignment } from '../types/sparkline-props.js';

interface GetContextValueLayoutProps {
  containerHeight: number;
  containerWidth: number;
  showContextValues: boolean;
  formattedContextMaxValue: string;
  formattedContextMinValue: string;
  labelsAlignment: SparklinLabelsAlignment;
  labelsFixedWidth?: number;
}

interface ContextValueLayout {
  fontSizePx: string;
  contextValueWidth: number;
  chartLeftOffset: number;
  chartWidth: number;
}

export function getContextValueLayout({
  containerHeight,
  containerWidth,
  showContextValues,
  formattedContextMaxValue,
  formattedContextMinValue,
  labelsAlignment,
  labelsFixedWidth,
}: GetContextValueLayoutProps): ContextValueLayout {
  const fontSizePx =
    containerHeight >= FONT_SIZE_BREAKING_POINT ? '12px' : '9px';

  const fontMap =
    containerHeight >= FONT_SIZE_BREAKING_POINT
      ? charsFontWidth12pixelsSize500Map
      : charsFontWidth9pixelsSize500Map;

  let contextValueWidth = labelsFixedWidth;

  if (contextValueWidth === undefined) {
    const stripSign = (s: string) =>
      s.startsWith(NEGATIVE_SIGN) ? s.slice(1) : s;
    const maxDigitStr = stripSign(formattedContextMaxValue);
    const minDigitStr = stripSign(formattedContextMinValue);

    const maxContextValueWidth = showContextValues
      ? Math.min(
          Math.ceil(calculateWidth(maxDigitStr, fontMap)),
          CONTEXT_VALUE_MAX_WIDTH,
        )
      : 0;

    const minContextValueWidth = showContextValues
      ? Math.min(
          Math.ceil(calculateWidth(minDigitStr, fontMap)),
          CONTEXT_VALUE_MAX_WIDTH,
        )
      : 0;

    // Reserve sign-column space only when context values are shown; this keeps
    // contextValueWidth at its minimal size (3 px) when values are hidden.
    const signColumnReservation = showContextValues
      ? containerHeight > CONTEXT_VALUE_SIGN_COLUMN_HEIGHT_THRESHOLD
        ? CONTEXT_VALUE_SIGN_COLUMN_LARGE
        : CONTEXT_VALUE_SIGN_COLUMN_SMALL
      : 0;

    contextValueWidth =
      Math.max(maxContextValueWidth, minContextValueWidth) +
      signColumnReservation +
      CONTEXT_VALUE_TRAILING_PADDING;
  }

  const chartLeftOffset =
    showContextValues && labelsAlignment === 'left' ? contextValueWidth : 0;

  const chartWidth = showContextValues
    ? containerWidth - contextValueWidth
    : containerWidth;

  return {
    fontSizePx,
    contextValueWidth,
    chartLeftOffset,
    chartWidth,
  };
}
